import { STAR } from "../../constants/imageConstants";
import { Lightbulb, Rocket, Webhook } from "lucide-react";
import { useHomepageData } from "../../hooks/useHomepageData";
import { CanvasRevealEffect } from "../../components/ui/canvas-reveal-effect";
import { AnimatePresence, motion } from "framer-motion";
import React from "react";

export default function BOTModelSection() {
  const { data } = useHomepageData();
  const botData = data.botModelSection;

  const getIconComponent = (iconName: string, size: number) => {
    switch (iconName) {
      case 'Lightbulb':
        return <Lightbulb size={size} />;
      case 'Webhook':
        return <Webhook size={size} />;
      case 'Rocket':
        return <Rocket size={size} />;
      default:
        return <Lightbulb size={size} />;
    }
  };

  return (
    <section className="relative px-4 lg:px-16 py-16 lg:py-20">
      {/* Desktop Layout */}
      <div className="hidden lg:flex gap-20 justify-center items-center flex-wrap">
        {botData.cards.map((card, index) => (
          <BotCard
            key={index}
            title={card.title}
            description={card.description}
            icon={getIconComponent(card.icon, 32)}
          />
        ))}
      </div>

      {/* Mobile Layout */}
      <div className="block lg:hidden space-y-20">
        {botData.cards.map((card, index) => (
          <BotCard
            key={index}
            title={card.title}
            description={card.description}
            icon={getIconComponent(card.icon, 28)}
          />
        ))}
      </div>
    </section>
  );
}

interface BotCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

function BotCard({ title, description, icon }: BotCardProps) {
  const [hovered, setHovered] = React.useState(false);

  // Create a proper SVG background with correct escaping
  const backgroundSvg = `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 375 314" preserveAspectRatio="none"><g transform="matrix(-0.92213 41.54 -49.609 -11.223 187.5 157)"><foreignObject x="-72.78" y="-72.78" width="145.56" height="145.56"><div xmlns="http://www.w3.org/1999/xhtml" style="background-image: conic-gradient(from 90deg, rgba(71, 47, 140, 0.4) 0%, rgba(54, 35, 105, 0.3) 11.991%, rgba(36, 23, 70, 0.2) 23.981%, rgba(0, 0, 0, 0) 47.962%, rgba(18, 13, 32, 0.25) 55.503%, rgba(37, 26, 65, 0.5) 63.044%, rgba(55, 40, 97, 0.75) 70.584%, rgba(73, 53, 130, 1) 78.125%, rgba(71, 47, 140, 0.4) 100%); opacity:0.3; height: 100%; width: 100%;"/></foreignObject></g></svg>`;

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bg-white/10 backdrop-blur-sm rounded-3xl min-h-80 border border-[#6A5555]/40 px-5 py-8 lg:p-8 relative w-full max-w-md mx-auto"
      style={{
        backgroundImage: `url('${backgroundSvg}')`,
      }}
    >
      {/* Canvas Reveal Effect on Hover */}
      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="h-full w-full absolute inset-0 rounded-3xl overflow-hidden"
          >
            <CanvasRevealEffect
              animationSpeed={5.1}
              containerClassName="bg-[#147af8]"
            />
            {/* Radial gradient for the cute fade */}
            <div className="absolute inset-0 [mask-image:radial-gradient(400px_at_center,white,transparent)] bg-black/50 dark:bg-black/90" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Decorative Stars */}
      <img
        className="absolute top-14 left-10 size-4 object-cover"
        src={STAR}
        alt="Decorative star"
      />
      <img
        className="absolute top-14 right-10 size-4 object-cover"
        src={STAR}
        alt="Decorative star"
      />

      <div className="flex flex-col justify-center items-center space-y-4 relative z-10">
        <div className="absolute -top-20 w-20 h-20 bg-gradient-to-b from-[#3982fd] to-[#bc98ff] rounded-full flex items-center justify-center border border-[#cb97ff]">
          {icon}
        </div>
        <h3 className="text-2xl lg:text-3xl text-white text-center capitalize">
          {title}
        </h3>
        <p className="text-base text-white leading-normal text-center">
          {description}
        </p>
      </div>
    </div>
  );
}
